import React, { Fragment, useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { useUnreadMessages } from '../contexts/UnreadMessagesContext';
import { Menu, Transition, Disclosure } from '@headlessui/react';
import { 
  Bars3Icon, 
  XMarkIcon, 
  ChevronDownIcon, 
  VideoCameraIcon,
  HomeIcon,
  UsersIcon,
  Cog6ToothIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../config/supabase';
import { MessageNotifications } from './MessageNotifications';
import { format } from 'date-fns';

// Utilidad para combinar clases CSS
function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface LayoutProps {
  children: React.ReactNode;
}

interface WebcamRef {
  cleanup: () => void;
}

const Layout = forwardRef<WebcamRef, LayoutProps>((props, ref) => {
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { hasUnreadMessages } = useUnreadMessages();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const lastActivityTime = useRef(new Date());
  const inactivityCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const webcamRef = useRef<WebcamRef | null>(null);

  // Exponer la referencia de la webcam
  useImperativeHandle(ref, () => ({
    cleanup: () => {
      if (webcamRef.current?.cleanup) {
        webcamRef.current.cleanup();
      }
    }
  }));

  // Función para actualizar el estado en profiles
  const updateProfileStatus = async (id: string, active: boolean) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: active })
        .eq('id', id);

      if (error) throw error;
    } catch (err) {
      console.error('Error updating profile status:', err);
    }
  };

  // Función para registrar actividad en activity_logs
  const logActivity = async (id: string, status: boolean, reason: string) => {
    try {
      // Primero, cerrar cualquier sesión abierta previa
      if (!status) {
        const { data: openSession } = await supabase
          .from('activity_logs')
          .select('*')
          .eq('employee_id', id)
          .is('end_time', null)
          .single();

        if (openSession) {
          await supabase
            .from('activity_logs')
            .update({ end_time: new Date().toISOString() })
            .eq('id', openSession.id);
        }
      }

      // Crear nuevo registro
      const { error } = await supabase
        .from('activity_logs')
        .insert({
          employee_id: id,
          status,
          start_time: new Date().toISOString(),
          end_time: status ? null : new Date().toISOString(),
          reason
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error logging activity:', err);
    }
  };

  // Verificar inactividad
  const checkInactivity = useCallback(async () => {
    if (!user?.id) return;

    try {
      // Primero verificar si es un empleado
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      // Solo verificar inactividad para empleados
      if (profileData.role !== 'employee') return;

      // Obtener el intervalo de inactividad configurado
      const { data: settings, error: settingsError } = await supabase
        .from('alert_settings')
        .select('interval_minutes')
        .eq('employee_id', user.id)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

      // Si no hay configuración de intervalo, no hacer nada
      if (!settings?.interval_minutes) return;

      const now = new Date();
      const diffMinutes = Math.floor((now.getTime() - lastActivityTime.current.getTime()) / 60000);

      // Solo marcar como inactivo si excedió el tiempo y está activo
      if (diffMinutes >= settings.interval_minutes && isActive) {
        setIsActive(false);
        
        // Actualizar estado en profiles (sin modificar last_activity)
        await supabase
          .from('profiles')
          .update({
            is_active: false
          })
          .eq('id', user.id);
        
        // Registrar en activity_logs
        await logActivity(user.id, false, 'Inactividad detectada');
      }
    } catch (error) {
      console.error('Error checking inactivity:', error);
    }
  }, [user, isActive]);

  // Manejar actividad del usuario
  const handleUserActivity = useCallback(async (event: Event) => {
    if (!user?.id) return;

    try {
      const { data: settings } = await supabase
        .from('alert_settings')
        .select('*')
        .eq('employee_id', user.id)
        .single();

      const now = new Date();
      const lastActivity = new Date(lastActivityTime.current);
      const diffMinutes = Math.floor((now.getTime() - lastActivity.getTime()) / 60000);

      // Solo actualizar última actividad y estado si hay interacción real
      if (event.type === 'mousemove' || event.type === 'keydown' || event.type === 'click') {
        // Actualizar última actividad
        lastActivityTime.current = now;

        // Si el usuario estaba inactivo, actualizar estado local
        if (!isActive) {
          setIsActive(true);
          
          // Registrar en activity_logs
          await logActivity(user.id, true, 'Actividad detectada');

          // Actualizar estado en profiles
          await supabase
            .from('profiles')
            .update({
              is_active: true,
              last_activity: now.toISOString()
            })
            .eq('id', user.id);
        } else {
          // Si está activo, solo actualizar last_activity
          await supabase
            .from('profiles')
            .update({
              last_activity: now.toISOString()
            })
            .eq('id', user.id);
        }
      }

      // Verificar inactividad si está activo
      if (settings?.interval_minutes && diffMinutes >= settings.interval_minutes && isActive) {
        setIsActive(false);
        
        // Actualizar estado en profiles (sin modificar last_activity)
        await supabase
          .from('profiles')
          .update({
            is_active: false
          })
          .eq('id', user.id);

        // Registrar inactividad
        await logActivity(user.id, false, 'Inactividad detectada');
      }
    } catch (err) {
      console.error('Error handling user activity:', err);
    }
  }, [user, isActive]);

  // Manejar actividad de la ventana
  useEffect(() => {
    const handleVisibilityChange = () => {
      // No actualizar lastActivityTime al cambiar visibilidad
      // Solo verificar inactividad
      if (document.visibilityState === 'visible') {
        checkInactivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkInactivity]);

  // Configurar verificación de inactividad
  useEffect(() => {
    if (!user?.id) return;

    // Obtener el intervalo configurado
    const setupInactivityCheck = async () => {
      try {
        const { data: settings } = await supabase
          .from('alert_settings')
          .select('interval_minutes')
          .eq('employee_id', user.id)
          .single();

        if (settings?.interval_minutes) {
          // Convertir minutos a milisegundos y usar la mitad del intervalo para verificar
          const checkInterval = (settings.interval_minutes * 60000) / 2;
          
          // Configurar el intervalo de verificación
          inactivityCheckInterval.current = setInterval(checkInactivity, checkInterval);
        }
      } catch (error) {
        console.error('Error setting up inactivity check:', error);
      }
    };

    setupInactivityCheck();

    return () => {
      if (inactivityCheckInterval.current) {
        clearInterval(inactivityCheckInterval.current);
        inactivityCheckInterval.current = null;
      }
    };
  }, [user, checkInactivity]);

  // Función de throttle para limitar la frecuencia de llamadas
  const throttle = (func: (...args: any[]) => void, limit: number) => {
    let inThrottle: boolean = false;
    return function(this: any, ...args: any[]) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => {
          inThrottle = false;
        }, limit);
      }
    };
  };

  // Configurar listeners de actividad
  useEffect(() => {
    const setupActivityListeners = () => {
      const throttledUpdateActivity = throttle(async (e: Event) => {
        if (user) {
          await handleUserActivity(e);
        }
      }, 60000);

      document.addEventListener('mousemove', throttledUpdateActivity);
      document.addEventListener('keydown', throttledUpdateActivity);
      document.addEventListener('click', throttledUpdateActivity);

      return () => {
        document.removeEventListener('mousemove', throttledUpdateActivity);
        document.removeEventListener('keydown', throttledUpdateActivity);
        document.removeEventListener('click', throttledUpdateActivity);
      };
    };

    return setupActivityListeners();
  }, [user, handleUserActivity]);

  const updateEmployeeStatus = async (id: string, online: boolean, reason: string) => {
    try {
      const { error } = await supabase
        .rpc('update_employee_status', {
          p_employee_id: id,
          p_status: online,
          p_reason: reason
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error updating employee status:', err);
    }
  };

  const updateActivity = useCallback(async () => {
    if (!user?.id) return;

    try {
      // Actualizar el estado del empleado a online
      await updateEmployeeStatus(user.id, true, 'activo');
    } catch (error) {
      console.error('Error updating activity:', error);
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      // Primero actualizar el estado del empleado si es necesario
      if (user?.id) {
        await updateEmployeeStatus(user.id, false, 'sesion_cerrada');
      }

      // Limpiar recursos de la webcam si existe
      const layoutRef = document.getElementById('layout-root')?.parentElement;
      if (layoutRef && '__webcamRef' in layoutRef) {
        // @ts-ignore - La referencia está configurada a través del Dashboard
        layoutRef.__webcamRef.cleanup();
      }
      
      // Usar el signOut del contexto de autenticación
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const navigation = [
    { name: 'Panel Principal', href: '/' },
    { name: 'Tareas', href: '/tasks' },
    { name: 'Chat', href: '/chat' },
    { name: 'Lista de Empleados', href: '/employee-list', role: 'supervisor' },
    { name: 'Monitor de Cámaras', href: '/webcam-monitor', role: 'supervisor' },
    { name: 'Gestión de Novedades', href: '/gestion-novedades', role: 'supervisor' },
    { name: 'Novedades', href: '/novedades', role: 'employee' },
  ];

  const informesMenu = [
    { name: 'Empleados', href: '/employee-list', role: 'supervisor' },
    { name: 'Reporte de Novedades', href: '/reporte-novedades', role: 'supervisor' }
  ];

  const configMenu = [
    { name: 'Configuración de Alertas', href: '/alert-settings', role: 'supervisor' },
    { name: 'Gestión de Novedades', href: '/gestion-novedades', role: 'supervisor' }
  ];

  const userMenu = [
    { name: 'Mi Perfil', href: '/profile' },
    { name: 'Cerrar Sesión', onClick: handleSignOut },
  ];

  // Actualizar el reloj cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Escuchar cambios en la sesión
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        console.log('Usuario ha sido desconectado');
        navigate('/login');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Verificar si la sesión ha sido invalidada
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('session_logs')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'session_logs',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          const updatedSession = payload.new as any;
          if (updatedSession.session_invalidated) {
            console.log('La sesión ha sido invalidada por un supervisor');
            await handleSignOut();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, handleSignOut]);

  return (
    <div id="layout-root" className="min-h-screen bg-gray-100">
      <MessageNotifications />
      {/* Barra superior */}
      <div className="flex items-center justify-between px-4 py-2 sm:px-6 lg:px-8">
        {/* Logo y menú móvil */}
        <div className="flex items-center">
          <div className="flex lg:hidden">
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Abrir menú principal</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="flex lg:flex-1">
            <Link to="/" className="text-xl font-bold text-indigo-600">
              Control Empleados
            </Link>
          </div>
        </div>

        {/* Navegación y perfil */}
        <div className="hidden lg:flex lg:gap-x-8">
          {navigation.map((item) => {
            if (item.role && item.role !== profile?.role) return null;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={classNames(
                  location.pathname === item.href
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  'inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium'
                )}
              >
                {item.name}
                {item.href === '/chat' && hasUnreadMessages && (
                  <span className="absolute top-2 -right-2 h-2 w-2 rounded-full bg-red-500 ring-1 ring-white" />
                )}
              </Link>
            );
          })}

          {/* Menú de Informes */}
          {profile?.role === 'supervisor' && (
            <Menu as="div" className="relative inline-flex items-center">
              <Menu.Button className={classNames(
                informesMenu.some(item => location.pathname === item.href)
                  ? 'border-indigo-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                'inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium'
              )}>
                Informes
                <ChevronDownIcon className="ml-1 h-4 w-4" aria-hidden="true" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute left-0 z-10 mt-14 w-48 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {informesMenu.map((item) => (
                    <Menu.Item key={item.name}>
                      {({ active }) => (
                        <Link
                          to={item.href}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            location.pathname === item.href ? 'text-indigo-600' : 'text-gray-700',
                            'block px-4 py-2 text-sm'
                          )}
                        >
                          {item.name}
                        </Link>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </Menu>
          )}

          {/* Menú de Configuración */}
          {profile?.role === 'supervisor' && (
            <Menu as="div" className="relative inline-flex items-center">
              <Menu.Button className={classNames(
                configMenu.some(item => location.pathname === item.href)
                  ? 'border-indigo-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                'inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium'
              )}>
                Configuración
                <ChevronDownIcon className="ml-1 h-4 w-4" aria-hidden="true" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute left-0 z-10 mt-14 w-48 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {configMenu.map((item) => (
                    <Menu.Item key={item.name}>
                      {({ active }) => (
                        <Link
                          to={item.href}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            location.pathname === item.href ? 'text-indigo-600' : 'text-gray-700',
                            'block px-4 py-2 text-sm'
                          )}
                        >
                          {item.name}
                        </Link>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </Menu>
          )}
        </div>

        {/* Perfil y notificaciones - visible en todos los tamaños */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <MessageNotifications />
          </div>
          <div className="text-sm text-gray-500">
            {format(currentTime, 'hh:mm:ss a')}
          </div>
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {profile?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </span>
              </div>
              <span className="hidden sm:ml-3 sm:block text-sm font-medium text-gray-900">
                {profile?.name || user?.email || 'Usuario'}
              </span>
              <ChevronDownIcon className="ml-2 h-5 w-5 text-gray-400" aria-hidden="true" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {userMenu.map((item) => (
                  <Menu.Item key={item.name}>
                    {({ active }) => (
                      item.href ? (
                        <Link
                          to={item.href}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'block px-4 py-2 text-sm text-gray-700'
                          )}
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <button
                          onClick={item.onClick}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'block w-full text-left px-4 py-2 text-sm text-gray-700'
                          )}
                        >
                          {item.name}
                        </button>
                      )
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      {/* Menú móvil */}
      <Transition
        show={mobileMenuOpen}
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between">
              <Link to="/" className="-m-1.5 p-1.5">
                <span className="text-lg font-bold text-indigo-600">Control Empleados</span>
              </Link>
              <button
                type="button"
                className="-m-2.5 rounded-md p-2.5 text-gray-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Cerrar menú</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  {navigation.map((item) => {
                    if (item.role && item.role !== profile?.role) return null;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={classNames(
                          location.pathname === item.href
                            ? 'bg-indigo-50 text-indigo-600'
                            : 'text-gray-700 hover:bg-gray-50',
                          'block rounded-lg px-3 py-2 text-base font-semibold leading-7'
                        )}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                        {item.href === '/chat' && hasUnreadMessages && (
                          <span className="ml-2 inline-block h-2 w-2 rounded-full bg-red-500" />
                        )}
                      </Link>
                    );
                  })}

                  {/* Menús adicionales en móvil */}
                  {profile?.role === 'supervisor' && (
                    <>
                      <div className="border-t border-gray-200 pt-4">
                        <div className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Informes
                        </div>
                        {informesMenu.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={classNames(
                              location.pathname === item.href
                                ? 'bg-indigo-50 text-indigo-600'
                                : 'text-gray-700 hover:bg-gray-50',
                              'block rounded-lg px-3 py-2 text-base font-semibold leading-7'
                            )}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>

                      <div className="border-t border-gray-200 pt-4">
                        <div className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Configuración
                        </div>
                        {configMenu.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={classNames(
                              location.pathname === item.href
                                ? 'bg-indigo-50 text-indigo-600'
                                : 'text-gray-700 hover:bg-gray-50',
                              'block rounded-lg px-3 py-2 text-base font-semibold leading-7'
                            )}
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="py-6">
                  {userMenu.map((item) => (
                    <div key={item.name}>
                      {item.href ? (
                        <Link
                          to={item.href}
                          className="block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <button
                          onClick={() => {
                            item.onClick?.();
                            setMobileMenuOpen(false);
                          }}
                          className="block w-full text-left rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                        >
                          {item.name}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      {/* Contenido principal */}
      <main className="flex-1">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="w-full">
            {props.children}
          </div>
        </div>
      </main>
    </div>
  );
});

Layout.displayName = 'Layout';

export default Layout;
